import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Layers, RotateCcw, Save } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { classesApi } from '@/features/institute/classes/api/classes.api';
import { ClassCurriculumSection } from '@/features/institute/classes/components/ClassSetupFields';
import type { ClassFormValues } from '@/features/institute/classes/schemas/class.schema';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import type { InstituteClass } from '@/types/instituteClass';
import { SUBJECT_CATEGORY_PLURAL, SUBJECT_CATEGORY_TONE } from '../constants';

type CurriculumValues = Pick<ClassFormValues, 'coreSubjects' | 'electives' | 'languages'>;

const toEntries = (names: string[]) => names.map((name) => ({ name }));
const toNames = (entries: { name: string }[] | undefined) =>
  (entries ?? []).map((entry) => entry.name.trim());

function curriculumOf(instituteClass: InstituteClass): CurriculumValues {
  return {
    coreSubjects: toEntries(instituteClass.coreSubjects),
    electives: toEntries(instituteClass.electives),
    languages: toEntries(instituteClass.languages),
  };
}

export interface ClassSubjectsViewProps {
  /** `CLASS_MANAGE` — without it the lists are shown, not edited. The server checks it again. */
  canManage: boolean;
  /**
   * The class to open on — from a link such as class setup's Manage. One that
   * is not in the list (suspended since, or another school's) falls back to the first.
   */
  initialClassUuid?: string;
  /**
   * Called after a class's lists are saved or the school's list is changed
   * from here, so the page's "Taught this year in" column is read again.
   */
  onChanged: () => void;
}

/**
 * The Subjects page seen one class at a time: pick a class, then say which of
 * the school's subjects it teaches this year.
 *
 * The same three lists, and the same save, as the class setup screen — this is
 * a second door to `class_subjects`, not a second store. So a change made here
 * is what class setup, the timetable, allocations and exams see next.
 */
export function ClassSubjectsView({
  canManage,
  initialClassUuid,
  onChanged,
}: ClassSubjectsViewProps) {
  const classes = useApiQuery(() => classesApi.list(), []);
  const options = useApiQuery(() => classesApi.setupOptions(), []);

  const [selectedUuid, setSelectedUuid] = useState<string | null>(initialClassUuid ?? null);
  /** A class picked while this one has unsaved changes, waiting on the confirmation. */
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);

  const form = useForm<ClassFormValues>();
  const {
    reset,
    setError,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  const classList = useMemo(() => classes.data ?? [], [classes.data]);
  const selected = classList.find((item) => item.uuid === selectedUuid) ?? classList[0];

  // Loads the picked class's lists, and reloads them after a save.
  useEffect(() => {
    if (selected) reset(curriculumOf(selected));
  }, [selected, reset]);

  function pick(uuid: string) {
    if (uuid === selected?.uuid) return;
    if (isDirty) {
      setPendingUuid(uuid);
      return;
    }
    setSelectedUuid(uuid);
  }

  const onSubmit = handleSubmit(async (values) => {
    if (!selected) return;
    try {
      await classesApi.setSubjects(selected.uuid, {
        coreSubjects: toNames(values.coreSubjects),
        electives: toNames(values.electives),
        languages: toNames(values.languages),
      });
      toast.success(`${selected.name} saved`, 'Its subjects for this year are updated.');
      classes.refetch();
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        toast.error(`Could not save ${selected.name}`, caught.message);
        return;
      }
      toast.error(`Could not save ${selected.name}`, 'Something went wrong. Please try again.');
    }
  });

  const error = classes.error ?? options.error;
  if (error) {
    return (
      <div className="p-4">
        <QueryError
          title="Could not load your classes"
          error={error}
          onRetry={() => {
            classes.refetch();
            options.refetch();
          }}
        />
      </div>
    );
  }

  if (classes.isLoading || options.isLoading) {
    return (
      <div className="p-4">
        <SkeletonLines count={5} />
      </div>
    );
  }

  if (!selected) {
    return (
      <EmptyState
        icon={<Layers className="h-5 w-5" />}
        title="No classes yet"
        description="Set up your classes first. Each class then picks its subjects from your school's list."
        action={
          canManage ? (
            <Link to="/admin/classes" className="text-sm font-semibold text-accent hover:underline">
              Go to classes
            </Link>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-3 sm:p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:max-w-xs">
          <Select
            label="Class"
            options={classList.map((item) => ({ value: item.uuid, label: item.name }))}
            value={selected.uuid}
            onChange={(event) => pick(event.target.value)}
            hint={`Subjects for ${selected.academicYear.name}.`}
          />
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="h-4 w-4" />}
              disabled={!isDirty || isSubmitting}
              onClick={() => reset(curriculumOf(selected))}
            >
              Discard
            </Button>
            <Button
              leftIcon={<Save className="h-4 w-4" />}
              disabled={!isDirty}
              isLoading={isSubmitting}
              onClick={() => void onSubmit()}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {canManage ? (
        <ClassCurriculumSection
          form={form}
          subjects={options.data?.subjects}
          onSubjectsChanged={() => {
            options.refetch();
            onChanged();
          }}
        />
      ) : (
        <dl className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {(
            [
              ['CORE', selected.coreSubjects],
              ['ELECTIVE', selected.electives],
              ['LANGUAGE', selected.languages],
            ] as const
          ).map(([category, names]) => (
            <div key={category} className="card flex flex-col gap-2 p-4">
              <dt className="lbl">{SUBJECT_CATEGORY_PLURAL[category]}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {names.length === 0 ? (
                  <span className="text-sm text-ink-3">None this year</span>
                ) : (
                  names.map((name) => (
                    <Badge key={name} tone={SUBJECT_CATEGORY_TONE[category]}>
                      {name}
                    </Badge>
                  ))
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <ConfirmDialog
        isOpen={Boolean(pendingUuid)}
        title={`Discard your changes to ${selected.name}?`}
        description="Its subjects have not been saved. Switching class drops what you changed."
        confirmLabel="Discard and switch"
        onConfirm={() => {
          setSelectedUuid(pendingUuid);
          setPendingUuid(null);
        }}
        onCancel={() => setPendingUuid(null)}
      />
    </div>
  );
}
